import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay';
import AlertMessage from '../../components/ui/AlertMessage';
import { useAuth } from '../../contexts/AuthContext';

// SVG Icon for the back button
const BackArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-arrow-left-short" viewBox="0 0 16 16">
        <path fillRule="evenodd" d="M12 8a.5.5 0 0 1-.5.5H5.707l2.147 2.146a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 1 1 .708.708L5.707 7.5H11.5A.5.5 0 0 1 12 8" />
    </svg>
);

// Define the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Helper to map status to a CSS class
const getStatusClass = (status) => {
    switch (status) {
        case 'Aprobada': return 'bg-success';
        case 'En Revisión': return 'bg-warning text-dark';
        case 'Rechazada': return 'bg-danger';
        case 'Pendiente': return 'bg-secondary';
        default: return 'bg-light text-dark';
    }
};

const EvaluationViewAdminScreen = () => {
    const { evaluationId } = useParams(); // Get ID from URL
    const { token } = useAuth(); // Get the authentication token
    const [evaluationDetails, setEvaluationDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [quizData, setQuizData] = useState(null);

    useEffect(() => {
        const fetchEvaluationDetails = async () => {
            setIsLoading(true);
            setError(null);

            try {
                // 1. Parse the raw ID from the URL parameter (e.g., "EVAL-000001" -> "1")
                const rawId = evaluationId.replace('EVAL-', '').replace(/^0+/, '');
                if (!rawId) throw new Error("ID de evaluación inválido.");

                // 2. Fetch the specific evaluation from the supplier-profiles endpoint
                const evalResponse = await fetch(`${API_BASE_URL}/api/supplier-profiles/${rawId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!evalResponse.ok) throw new Error('No se pudo obtener el detalle de la evaluación.');
                let evaluationData = await evalResponse.json(); // Use let to allow modification

                // If status is "Pendiente", update it to "En Revisión" automatically
                if (evaluationData.status === 'Pendiente') {
                    try {
                        const updateResponse = await fetch(`${API_BASE_URL}/api/supplier-profiles/${rawId}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                            body: JSON.stringify({ status: 'En Revisión' }),
                        });
                        if (updateResponse.ok) {
                            // The status was successfully updated on the backend.
                            // We manually update our local data object to reflect this change
                            // without needing to parse the response body.
                            console.log('Estado actualizado automáticamente a "En Revisión"');
                            evaluationData.status = 'En Revisión';
                        } else {
                            console.error("No se pudo actualizar automáticamente el estado a 'En Revisión'");
                        }
                    } catch (updateError) {
                        console.error("Error al actualizar automáticamente el estado:", updateError);
                    }
                }

                // 3. Fetch supplier details to get the name
                let providerName = 'Nombre no disponible';
                if (evaluationData.supplier_id) {
                    const supplierResponse = await fetch(`${API_BASE_URL}/api/suppliers/${evaluationData.supplier_id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (supplierResponse.ok) {
                        const supplierData = await supplierResponse.json();
                        providerName = supplierData.name;
                    }
                }

                console.log("Answers: " + JSON.stringify(evaluationData))

                setEvaluationDetails({
                    id: `EVAL-${String(evaluationData.id).padStart(6, '0')}`,
                    providerName: providerName,
                    creationDate: evaluationData.created_at ? new Date(evaluationData.created_at).toLocaleDateString() : 'N/A',
                    type: evaluationData.type,
                    score: evaluationData.score >= 0 ? `${evaluationData.score}/100` : 'N/A',
                    status: evaluationData.status,
                    rejection_reason: evaluationData.rejection_reason || null, // Include rejection reason
                    statusClass: getStatusClass(evaluationData.status),
                    answer: evaluationData.answer || {}, // The API returns an 'answer' object
                });

                // 6. Fetch the specific quiz from the quizzes endpoint
                const quizResponse = await fetch(`${API_BASE_URL}/api/quizzes`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!quizResponse.ok) throw new Error('No se pudo obtener el detalle del quiz.');
                const quizzesData = await quizResponse.json();

                // 7. Seek the one which name is Supplier Questionnaire
                const supplierQuestionnaire = quizzesData.find(quiz => quiz.name === 'Sello Checkin');

                if (supplierQuestionnaire) {

                    // 8. If such quiz exists, load it completely from the endpoint
                    const quizDataResponse = await fetch(`${API_BASE_URL}/api/quizzes/${supplierQuestionnaire.id}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (!quizDataResponse.ok) throw new Error('No se pudo obtener los datos del quiz.');
                    const tempData = await quizDataResponse.json();
                    console.log(tempData)
                    setQuizData(tempData);

                } else {
                    throw new Error('No se pudo obtener el detalle del quiz.');
                }


            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        if (token && evaluationId) {
            fetchEvaluationDetails();
        }
    }, [evaluationId, token]);

    const questionMap = useMemo(() => {
        if (!quizData?.questions) {
            return new Map();
        }
        return new Map(quizData.questions.map(q => [q.id, q]));
    }, [quizData]);

    // Helper function to render different types of answers
    const renderAnswerValue = (answerValue, questionSchema) => {
        if (answerValue === null || answerValue === undefined || answerValue === '') {
            return <span className="text-muted">No respondida</span>;
        }

        // Collect all options from options and sections
        const allOptions = [...(questionSchema.options || [])];
        if (questionSchema.sections && Array.isArray(questionSchema.sections)) {
            questionSchema.sections.forEach(section => {
                if (section.options && Array.isArray(section.options)) {
                    allOptions.push(...section.options);
                }
            });
        }

        // Helper to get label from value
        const getLabel = (val) => {
            const option = allOptions.find(opt => opt.value === val);
            if (option) {
                // Prefer text, then label, then mainText, then id as fallback
                return option.text || option.label || option.mainText || option.id || val;
            }
            return val;
        };

        // Handle multiSectionCheckboxGroup and multiSectionSelectableOption answers (object with selections and otherDetail)
        if ((questionSchema.type === 'multiSectionCheckboxGroup' || questionSchema.type === 'multiSectionSelectableOption') && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
            const selections = answerValue.selections || [];
            if (selections.length === 0) return <span className="text-muted">No respondida</span>;
            return selections.map(val => getLabel(val)).join(', ');
        }

        // Handle composite answers (e.g., checkboxGroupWithOther, selectableOptionWithOther)
        if (questionSchema.type.includes('WithOther') && typeof answerValue === 'object' && !Array.isArray(answerValue)) {
            const selectionsKey = Object.keys(answerValue).find(k => Array.isArray(answerValue[k])) || 'selections';
            const otherDetailKey = Object.keys(answerValue).find(k => typeof answerValue[k] === 'string' && k !== selectionsKey) || 'otherDetail';

            const selections = answerValue[selectionsKey] || [];
            const otherDetail = answerValue[otherDetailKey] || '';
            const otherConfig = questionSchema.otherInputConfig;

            if (selections.length === 0) return <span className="text-muted">No respondida</span>;

            const labels = selections
                .map(val => {
                    if (otherConfig && val === otherConfig.optionValue) {
                        return otherDetail ? `${otherConfig.optionLabel}: ${otherDetail}` : otherConfig.optionLabel;
                    }
                    return getLabel(val);
                })
                .filter(Boolean)
                .join(', ');

            return labels || <span className="text-muted">No respondida</span>;
        }

        // Handle multiSectionCheckboxGroup answers (array of values)
        if (Array.isArray(answerValue)) {
            if (answerValue.length === 0) return <span className="text-muted">No respondida</span>;
            return answerValue.map(val => getLabel(val)).join(', ');
        }

        // Handle numberInputWithToggle with dynamic keys from schema (e.g., {amount: "1", noFixedBudget: false})
        if (questionSchema.type === 'numberInputWithToggle' && typeof answerValue === 'object' && answerValue !== null) {
            const initialVal = questionSchema.initialValue || { amount: '', noFixedBudget: false };
            const amountKey = Object.keys(initialVal).find(k => typeof initialVal[k] === 'string' || typeof initialVal[k] === 'number') || 'amount';
            const toggleKey = Object.keys(initialVal).find(k => typeof initialVal[k] === 'boolean') || 'noFixedBudget';
            if (answerValue[toggleKey]) {
                return <span>No aplica</span>;
            }
            return <span>{answerValue[amountKey]}</span>;
        }

        // Handle numberInput with object answer (e.g., {amount: "1", noApply: false})
        if (typeof answerValue === 'object' && answerValue !== null) {
            // Try to stringify relevant keys if present
            if ('amount' in answerValue && 'noApply' in answerValue) {
                if (answerValue.noApply) {
                    return <span>No aplica</span>;
                }
                return <span>{answerValue.amount}</span>;
            }
            if ('startDate' in answerValue && 'endDate' in answerValue) {
                return <span>{answerValue.startDate} a {answerValue.endDate}</span>;
            }
            // Fallback: JSON stringify
            return <pre>{JSON.stringify(answerValue, null, 2)}</pre>;
        }

        // Handle single value answers
        const option = allOptions.find(opt => opt.value === String(answerValue));
        return option ? (option.text || option.label || option.mainText || option.id || String(answerValue)) : String(answerValue);
    };

    const handleApprove = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const rawId = evaluationDetails.id.replace('EVAL-', '').replace(/^0+/, '');
            const response = await fetch(`${API_BASE_URL}/api/supplier-profiles/${rawId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ status: 'Aprobada' }),
            });
            if (!response.ok) throw new Error('No se pudo aprobar la evaluación.');
            // Manually update the state on success, no need to parse response body.
            setEvaluationDetails(prev => ({
                ...prev,
                status: 'Aprobada',
                statusClass: getStatusClass('Aprobada')
            }));
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReject = () => {
        setShowRejectModal(true);
    };

    const handleConfirmReject = async () => {
        if (!rejectionReason.trim()) return;
        setIsLoading(true);
        setError(null);
        try {
            const rawId = evaluationDetails.id.replace('EVAL-', '').replace(/^0+/, '');
            const response = await fetch(`${API_BASE_URL}/api/supplier-profiles/${rawId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    status: 'Rechazada',
                    rejection_reason: rejectionReason
                }),
            });
            if (!response.ok) throw new Error('No se pudo rechazar la evaluación.');
            // Manually update the state on success.
            setEvaluationDetails(prev => ({
                ...prev,
                status: 'Rechazada',
                statusClass: getStatusClass('Rechazada')
            }));
            setShowRejectModal(false);
            setRejectionReason('');
        } catch (err) {
            setError(err.message);
            // Keep modal open on error so the user can retry
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) {
        return <FormSpinnerOverlay isLoading={true} />;
    }

    if (error) {
        return <AlertMessage type="danger" message={`Error al cargar el detalle de la evaluación: ${error}`} />;
    }

    if (!evaluationDetails) {
        return <AlertMessage type="info" message="No se encontraron detalles para esta evaluación." />;
    }

    const showActionButtons = evaluationDetails.status !== 'Aprobada' && evaluationDetails.status !== 'Rechazada';

    return (
        <>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <h1 className="h2 mb-md-0 mb-4">Detalle de Evaluación <span className="text-muted fs-5">({evaluationDetails.id})</span></h1>
                <div className="btn-toolbar mb-2 mb-md-0">
                    <Link to="/evaluations" className="btn btn-outline-secondary me-2">
                        <BackArrowIcon />
                        Volver a Evaluaciones
                    </Link>
                </div>
            </div>

            <div className="row text-start mb-4">
                <div className="col-12">
                    <div className="card border-0">
                        <div className="card-body px-md-4 px-3 pb-5">
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">ID Evaluación:</h5>
                                    <p>{evaluationDetails.id}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Nombre Proveedor:</h5>
                                    <p>{evaluationDetails.providerName}</p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Fecha de Creación:</h5>
                                    <p>{evaluationDetails.creationDate}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Tipo de Evaluación:</h5>
                                    <p>{evaluationDetails.type}</p>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Puntaje Obtenido:</h5>
                                    <p>{evaluationDetails.score}</p>
                                </div>
                                <div className="col-md-6 mb-3">
                                    <h5 className="text-muted">Estado Actual:</h5>
                                    <p><span className={`badge rounded-pill ${evaluationDetails.statusClass}`}>{evaluationDetails.status}</span></p>
                                </div>
                            </div>

                            {evaluationDetails.status === 'Rechazada' && evaluationDetails.rejection_reason && (
                                <>
                                    <hr className="my-4" />
                                    <h5 className="mb-4 text-danger">Motivo del Rechazo:</h5>
                                    <div className="alert alert-danger" role="alert">
                                        {evaluationDetails.rejection_reason}
                                    </div>
                                </>
                            )}

                            <hr className="my-4" />


                            <h5 className="mb-4">Respuestas del Proveedor</h5>
                            <div className="mb-3" id="supplierAnswersContainer">
                                {evaluationDetails.answer && Object.keys(evaluationDetails.answer).length > 0 && quizData ? (
                                    Object.entries(evaluationDetails.answer).map(([questionId, answerValue]) => {
                                        const questionSchema = questionMap.get(questionId);
                                        if (!questionSchema || questionSchema.type === 'hiddenInput') return null;

                                        return (
                                            <div key={questionId} className="mb-4">
                                                <div className="ps-md-2 mb-4">
                                                    <h4 className="supplier-question h6 fw-bold">{questionSchema.title}</h4>
                                                    <div className="supplier-answer">{renderAnswerValue(answerValue, questionSchema)}</div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <AlertMessage type="info" message="No se han encontrado respuestas para esta evaluación." />
                                )}
                            </div>

                            {showActionButtons && (
                                <div className="d-flex justify-content-end mt-4">
                                    <button type="button" className="btn btn-success d-flex align-items-center me-2" onClick={handleApprove}>
                                        Aprobar Evaluación
                                    </button>
                                    <button type="button" className="btn btn-danger d-flex align-items-center" onClick={handleReject}>
                                        Rechazar Evaluación
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal Motivo Rechazo */}
            {showRejectModal && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="rechazarEvaluacionModalLabel" aria-hidden="true">
                    <div className="modal-dialog" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="rechazarEvaluacionModalLabel">Motivo del Rechazo</h5>
                                <button type="button" className="btn-close" onClick={() => setShowRejectModal(false)} aria-label="Close"></button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    <div className="mb-3">
                                        <label htmlFor="motivoRechazoTexto" className="form-label">Por favor, especifique el motivo del rechazo:</label>
                                        <textarea
                                            className="form-control"
                                            id="motivoRechazoTexto"
                                            rows="4"
                                            placeholder="Ingrese aquí el motivo..."
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            required
                                        ></textarea>
                                    </div>
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                                <button type="button" className="btn btn-danger" onClick={handleConfirmReject} disabled={!rejectionReason.trim()}>Confirmar Rechazo</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {showRejectModal && <div className="modal-backdrop fade show"></div>} {/* Manual backdrop */}
        </>
    );
};

export default EvaluationViewAdminScreen;